import { useEffect, useRef, useState, type ReactElement } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useLogin } from "react-admin";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import {
  GoogleAuthProvider,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithPopup,
  type ConfirmationResult,
} from "firebase/auth";
import { MuiTelInput } from "mui-tel-input";

import { authObj, recaptchaConfigReady } from "@/firebase-config";
import { finalizeFirebaseLogin } from "@/auth-provider";
import { AuthBrandedShell } from "@/layout/AuthBrandedShell";
import {
  getFirebaseGoogleAuthMessage,
  getFirebasePhoneAuthMessage,
} from "@/utils/firebase-auth-messages";

const LOGIN_PHONE_RECAPTCHA_ID = "login-phone-recaptcha";
type AuthMode = "email" | "phone";

function getLoginReason(): string | null {
  if (typeof window === "undefined") return null;
  const fromSearch = new URLSearchParams(window.location.search).get("reason");
  if (fromSearch) return fromSearch;
  const hash = window.location.hash;
  const idx = hash.indexOf("?");
  if (idx < 0) return null;
  return new URLSearchParams(hash.slice(idx + 1)).get("reason");
}

export default function Login(): ReactElement {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState<AuthMode>("email");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [loadingPhone, setLoadingPhone] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneInfo, setPhoneInfo] = useState<string | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);
  const [loginReason] = useState<string | null>(() => getLoginReason());
  const login = useLogin();

  const normalizePhoneNumber = (value: string): string => value.trim().replace(/[\s\-()]/g, "");

  useEffect(
    () => () => {
      recaptchaRef.current?.clear();
      recaptchaRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setOtpCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpCooldown]);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setEmailError(null);
    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    setLoadingEmail(true);
    login({ username: trimmedUsername, password: trimmedPassword })
      .then(() => {
        setLoadingEmail(false);
      })
      .catch((error: unknown) => {
        setEmailError(error instanceof Error ? error.message : "Invalid email or password");
        setLoadingEmail(false);
      });
  };

  const handleSendOtp = async (): Promise<void> => {
    setPhoneError(null);
    setPhoneInfo(null);
    const normalized = normalizePhoneNumber(phoneNumber);
    if (!/^\+[1-9]\d{7,14}$/.test(normalized)) {
      setPhoneError("Enter phone in E.164 format (e.g. +919876543210)");
      return;
    }
    if (otpCooldown > 0) {
      setPhoneInfo(`Please wait ${otpCooldown}s before requesting a new OTP`);
      return;
    }
    setLoadingPhone(true);
    try {
      await recaptchaConfigReady;
      recaptchaRef.current?.clear();
      recaptchaRef.current = new RecaptchaVerifier(authObj, LOGIN_PHONE_RECAPTCHA_ID, {
        size: "invisible",
      });
      const verifier = recaptchaRef.current;
      await verifier.render();
      const confirmationResult = await signInWithPhoneNumber(authObj, normalized, verifier);
      setConfirmation(confirmationResult);
      setOtpCooldown(30);
      setPhoneInfo("OTP sent. Enter the code below.");
    } catch (error: unknown) {
      setPhoneError(getFirebasePhoneAuthMessage(error));
    } finally {
      setLoadingPhone(false);
    }
  };

  const handleGoogleLogin = async (): Promise<void> => {
    setEmailError(null);
    setLoadingGoogle(true);
    try {
      const provider = new GoogleAuthProvider();
      const credential = await signInWithPopup(authObj, provider);
      const { redirectTo } = await finalizeFirebaseLogin(credential.user);
      navigate(redirectTo);
    } catch (error: unknown) {
      setEmailError(getFirebaseGoogleAuthMessage(error));
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleVerifyOtp = async (): Promise<void> => {
    setPhoneError(null);
    const trimmedOtp = otpCode.trim();
    if (!confirmation) {
      setPhoneError("Please send OTP first");
      return;
    }
    if (!trimmedOtp) {
      setPhoneError("Please enter OTP");
      return;
    }
    setLoadingPhone(true);
    try {
      const credential = await confirmation.confirm(trimmedOtp);
      const { redirectTo } = await finalizeFirebaseLogin(credential.user);
      navigate(redirectTo);
    } catch (error: unknown) {
      setPhoneError(getFirebasePhoneAuthMessage(error));
    } finally {
      setLoadingPhone(false);
    }
  };

  return (
    <AuthBrandedShell showLogo={false}>
      <Paper
        elevation={0}
        sx={(theme) => ({
          width: "100%",
          maxWidth: 380,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          borderRadius: 2,
          p: 2,
          boxShadow: theme.shadows[0],
        })}
      >
        {loginReason === "unauthorized" ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You don&apos;t have access to this app. Ask an administrator if you need help.
          </Alert>
        ) : null}
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Sign in
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Continue with email or phone verification.{" "}
          <Link component={RouterLink} to="/fmb-thali-distributor/login">
            Distributor portal sign-in
          </Link>
        </Typography>

        <Tabs
          value={authMode}
          onChange={(_, value: AuthMode) => {
            setAuthMode(value);
            setEmailError(null);
            setPhoneError(null);
            setPhoneInfo(null);
          }}
          variant="fullWidth"
          sx={{ mb: 1 }}
        >
          <Tab value="email" label="Email" />
          <Tab value="phone" label="Phone" />
        </Tabs>

        {authMode === "email" ? (
          <Box component="form" noValidate onSubmit={handleSubmit}>
            {emailError ? (
              <Alert severity="error" sx={{ mb: 1 }}>
                {emailError}
              </Alert>
            ) : null}
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              variant="outlined"
              autoFocus
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              variant="outlined"
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <Box sx={{ textAlign: "right", mt: 0.5 }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                Forgot password?
              </Link>
            </Box>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loadingEmail || loadingPhone || loadingGoogle}
              sx={{ mt: 2, height: "40px", borderRadius: 3 }}
            >
              Sign In
              {loadingEmail && <CircularProgress color="inherit" size={20} sx={{ ml: 1 }} />}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleGoogleLogin}
              disabled={loadingEmail || loadingPhone || loadingGoogle}
              startIcon={
                <Box
                  component="img"
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt=""
                  sx={{ width: 18, height: 18 }}
                />
              }
              sx={(theme) => ({
                mt: 1,
                height: "40px",
                borderRadius: 3,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? theme.palette.background.default
                    : theme.palette.common.white,
                borderColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.action.selected
                    : theme.palette.divider,
                color: theme.palette.text.primary,
                textTransform: "none",
                "&:hover": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.action.focus
                      : theme.palette.divider,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? theme.palette.action.selected
                      : theme.palette.action.hover,
                },
              })}
            >
              Sign in with Google
              {loadingGoogle && <CircularProgress color="inherit" size={20} sx={{ ml: 1 }} />}
            </Button>
          </Box>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {phoneError ? <Alert severity="error">{phoneError}</Alert> : null}
            {!phoneError && phoneInfo ? <Alert severity="info">{phoneInfo}</Alert> : null}
            <Typography variant="body2" color="text.secondary">
              Enter your linked phone number to receive a one-time code.
            </Typography>
            <MuiTelInput
              fullWidth
              label="Phone number"
              value={phoneNumber}
              onChange={(value) => setPhoneNumber(value)}
              disabled={loadingPhone}
              defaultCountry="IN"
              forceCallingCode
              langOfCountryName="en"
            />
            <Button
              variant="outlined"
              onClick={handleSendOtp}
              disabled={loadingPhone || otpCooldown > 0}
              fullWidth
              sx={{ height: "40px", borderRadius: 3 }}
            >
              {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Send OTP"}
            </Button>
            {confirmation ? (
              <>
                <TextField
                  fullWidth
                  label="OTP code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  disabled={loadingPhone}
                />
                <Button
                  variant="contained"
                  onClick={handleVerifyOtp}
                  disabled={loadingPhone}
                  fullWidth
                  sx={{ height: "40px", borderRadius: 3 }}
                >
                  Verify OTP
                  {loadingPhone && <CircularProgress color="inherit" size={20} sx={{ ml: 1 }} />}
                </Button>
              </>
            ) : null}
          </Stack>
        )}
      </Paper>
      <Box id={LOGIN_PHONE_RECAPTCHA_ID} />
    </AuthBrandedShell>
  );
}
